<template>
  <div class="view" v-if="canView || canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :grid-ref="$refs.smProductsGrid"
      :title="$t('common.products')"
      :show-add="canEdit"
      :show-filter="false"
      @add="newObject"
      :show-refresh="true"
    />

    <DxDataGrid
      ref="smProductsGrid"
      :data-source="smProductsDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      :selected-row-keys="selectedRowUuids"
      @editing-start="onEditingStart"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
      @content-ready="onContentReady"
      @row-click="onRowClick"
      @selection-changed="onSelectionChanged"
    >
      <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
      <DxColumn
        data-field="name"
        :caption="$t('common.product_name')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :width="250"
      />
      <DxColumn
        data-field="barcode"
        :caption="$t('stock_management.products.barcode')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :width="130"
      />
      <DxColumn
        data-field="code"
        :caption="$t('stock_management.products.code')"
        data-type="string"
        alignment="left"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :width="200"
      />
      <DxColumn
        data-field="product_type.name"
        :caption="$t('stock_management.products.type')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="measure_unit.name"
        :caption="$t('common.measure_unit')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="lot_management"
        :caption="$t('stock_management.products.lot_management')"
        data-type="number"
        alignment="left"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        :width="130"
        :calculateCellValue="getLotManagementColumnText"
        cell-template="lotManagementCell"
      >
        <DxLookup
          :data-source="yes_no_list"
          value-expr="id"
          display-expr="label"
        />
      </DxColumn>
      <template #lotManagementCell="{ data }">
        <span>
          {{ getYesNoColumnText(data?.data?.lot_management) }}
        </span>
      </template>
      <DxColumn
        data-field="is_active"
        :caption="$t('common.active')"
        data-type="number"
        alignment="left"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="140"
        :calculateCellValue="getIsActiveColumnText"
        cell-template="isActiveViewTemplate"
      >
        <DxLookup :data-source="active_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>{{ getActiveStatusName(data.data.is_active) }}</span>
      </template>
      <DxColumn
        data-field="created_at"
        :caption="$t('common.created_at')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="getDxGridDefaultDateFormat"
      />
      <DxColumn
        data-field="updated_at"
        :caption="$t('common.last_update')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="updatedAtDateTimeFormat"
      />
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.smProductsGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
          :edit-action="onEdit"
          :items="getIntervalPriceMenuItems(data.data)"
          :menu-width="210"
        />
      </template>

      <DxEditing mode="row" :allow-updating="false" :allow-adding="false" :allow-deleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('common.products'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
    <GridGroupActions
      ref="groupActions"
      :grid-ref="$refs.smProductsGrid"
      :selected-count="selectedRowUuidsAll.length"
      @cleared="onSelectionCleared"
    >
      <b-dropdown
        dropup
        :text="$t('stock_management.products.toggle_lot_management')"
        :disabled="isUpdatingLotManagement || selectedRowUuidsAll.length === 0"
        class="smagria-dropdown bulk-lot-management-dropdown"
      >
        <b-dropdown-item @click="setLotManagementForSelected(1)">{{ $t("common.yes") }}</b-dropdown-item>
        <b-dropdown-item @click="setLotManagementForSelected(0)">{{ $t("common.no") }}</b-dropdown-item>
      </b-dropdown>
    </GridGroupActions>
    <ProductDateIntervalUnitPricePopup
      v-if="intervalPricePopupVisible && intervalPriceProduct"
      :visible="intervalPricePopupVisible"
      :product="intervalPriceProduct"
      @close="closeIntervalPricePopup"
      @save="closeIntervalPricePopup"
    />
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxSelection,
  DxEditing,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxLookup,
  DxTexts,
} from "devextreme-vue/data-grid";
import { actions, canDelete, canEdit, canView, hasPermission, permissions } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridGroupActions from "@/components/DxGrid/GridGroupActions.vue";
import ProductDateIntervalUnitPricePopup from "./ProductDateIntervalUnitPricePopup.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import { handleError, bulkAction } from "@/helpers/generalActions";

export default {
  name: "SMProductList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      filterPanelVisible: false,
      selectedRowUuids: [],
      selectedRowUuidsAll: [],
      grid_data_uuids: [],
      isRestoringSelection: false,
      isUpdatingLotManagement: false,
      intervalPricePopupVisible: false,
      intervalPriceProduct: null,
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
      active_list: [
        {
          id: 1,
          label: this.$t("common.active"),
        },
        {
          id: 0,
          label: this.$t("common.inactive"),
        },
      ],
      smProductsDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const dataSource = {
            data: [],
            totalCount: 0,
          };

          try {
            const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            const payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            const response = await this.getSmProducts(payload);
            dataSource.data = response?.data ?? [];
            dataSource.totalCount = response?.pagination?.total ?? 0;
            this.grid_data_uuids = Array.isArray(response?.data) ? response.data.map((item) => item.uuid) : [];
            return dataSource;
          } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
          }
        },
        remove: this.onDelete,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
    };
  },
  components: {
    Forbidden,
    GridPanelHeader,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxEditing,
    DxGroupPanel,
    DxColumnChooser,
    DxLookup,
    DxColumnFixing,
    DxExport,
    DxActionMenu,
    DxTexts,
    GridGroupActions,
    ProductDateIntervalUnitPricePopup,
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      user: "auth/user",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid sm-products-grid",
      };
    },
    updatedAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    canManageIntervalPrices() {
      return hasPermission({}, this.user, permissions.SM_PRODUCT_INTERVAL_UNIT_PRICES, actions.EDIT);
    },
  },
  methods: {
    ...mapActions({
      getSmProducts: "smProducts/getSmProducts",
      deleteSmProduct: "smProducts/deleteSmProduct",
      updateSmProduct: "smProducts/updateSmProduct",
    }),
    newObject() {
      this.$router.push({
        path: "/stock-management/sm-products/add",
      });
    },
    allowDeleting(row) {
      return this.canDelete && row && row.is_editable !== false;
    },
    onEditingStart(e) {
      e.cancel = true;
      if (e && e.data) {
        this.onEdit(e.data);
      }
    },
    onEdit(row) {
      if (!row || !row.uuid) return;
      this.$router.push({ path: `/stock-management/sm-products/${row.uuid}` });
    },
    getIntervalPriceMenuItems(row) {
      if (!this.canManageIntervalPrices) return [];
      return [{
        key: "interval-unit-prices",
        label: this.$t("stock_management.products.interval_prices.add_edit"),
        position: 1,
        icon: require("@/assets/images/icons/list.svg"),
        action: () => this.openIntervalPricePopup(row),
      }];
    },
    openIntervalPricePopup(row) {
      this.intervalPriceProduct = row;
      this.intervalPricePopupVisible = true;
    },
    closeIntervalPricePopup() {
      this.intervalPricePopupVisible = false;
      this.intervalPriceProduct = null;
    },
    async onDelete(key) {
      if (!key || !this.canDelete) return;

      try {
        await this.deleteSmProduct({ uuid: key });
        this.reloadGrid();
      } catch (e) {
        handleError(this, e);
      }
    },
    onSelectionChanged(e) {
      this.selectedRowUuids = Array.isArray(e.selectedRowKeys) ? e.selectedRowKeys : [];

      if (this.isRestoringSelection) {
        return;
      }

      const currentSelected = Array.isArray(e?.currentSelectedRowKeys) ? e.currentSelectedRowKeys : [];
      const currentDeselected = Array.isArray(e?.currentDeselectedRowKeys) ? e.currentDeselectedRowKeys : [];

      currentSelected.forEach((key) => {
        if (this.grid_data_uuids.includes(key) && !this.selectedRowUuidsAll.includes(key)) {
          this.selectedRowUuidsAll.push(key);
        }
      });

      currentDeselected.forEach((key) => {
        if (this.grid_data_uuids.includes(key)) {
          const index = this.selectedRowUuidsAll.indexOf(key);
          if (index !== -1) {
            this.selectedRowUuidsAll.splice(index, 1);
          }
        }
      });
    },
    onRowClick(e) {
      if (!e || !e.component || e.rowType !== "data") return;
      const key = e.key;
      const instance = e.component;
      const isSelected = instance.isRowSelected(key);
      if (isSelected) {
        instance.deselectRows([key]);
        return;
      }
      instance.selectRows([key], true);
    },
    onSelectionCleared() {
      this.selectedRowUuids = [];
      this.selectedRowUuidsAll = [];
      if (this.$refs?.smProductsGrid?.instance) {
        this.$refs.smProductsGrid.instance.selectRows([], false);
      }
    },
    onContentReady() {
      const instance = this.$refs?.smProductsGrid?.instance;
      if (instance) {
        const items = instance.getDataSource()?.items?.() ?? [];
        this.grid_data_uuids = Array.isArray(items) ? items.map((item) => item.uuid) : [];
      }
      this.restoreSelection();
    },
    restoreSelection() {
      const instance = this.$refs?.smProductsGrid?.instance;
      if (!instance) {
        return;
      }

      this.isRestoringSelection = true;
      this.selectedRowUuids = this.selectedRowUuidsAll.filter((uuid) => this.grid_data_uuids.includes(uuid));
      instance.selectRows(this.selectedRowUuids, false);
      this.$nextTick(() => {
        this.isRestoringSelection = false;
      });
    },
    getYesNoColumnText(value) {
      if (value === null || typeof value === "undefined") {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    getLotManagementColumnText(rowData) {
      return this.getYesNoColumnText(rowData?.lot_management);
    },
    getActiveStatusName(value) {
      return this.active_list.find((item) => item.id == value)?.label ?? "";
    },
    getIsActiveColumnText(rowData) {
      return this.getActiveStatusName(rowData?.is_active);
    },
    async setLotManagementForSelected(nextValue) {
      if (!this.selectedRowUuidsAll.length || this.isUpdatingLotManagement) {
        return;
      }

      this.isUpdatingLotManagement = true;
      try {
        const groupActions = this.$refs?.groupActions;
        const runBulkAction = groupActions && typeof groupActions.runBulkAction === "function" ? groupActions.runBulkAction : bulkAction;

        const result = await runBulkAction.call(groupActions || this, {
          endpoint: "/stock-management/products/bulk",
          action: "update",
          uuids: this.selectedRowUuidsAll,
          data: { lot_management: nextValue },
          confirmTitle: this.$t("bulk_actions.confirm_title"),
          confirmMessage: this.$t("bulk_actions.confirm_update", { count: this.selectedRowUuidsAll.length }),
          emptyMessage: this.$t("bulk_actions.no_selection"),
          successMessageBuilder: (resp) => {
            return this.$t("bulk_actions.updated_summary", {
              updated: resp?.data?.updated ?? 0,
            });
          },
        });
        if (result && result.cancelled) {
          return;
        }
        this.reloadGrid();
        this.onSelectionCleared();
      } catch (e) {
        handleError(this, e);
      } finally {
        this.isUpdatingLotManagement = false;
      }
    },
    reloadGrid() {
      const instance = this.$refs?.smProductsGrid?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
    onExporting(e) {
      e.fileName = "SMProducts_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const fields = ["lot_management"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SMProducts");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (!gridCell?.data) {
              return;
            }

            if (fields.includes(gridCell?.column?.dataField)) {
              excelCell.value = this.getYesNoColumnText(gridCell?.data?.[gridCell?.column?.dataField]);
            } else if (gridCell?.column?.dataField === "is_active") {
              excelCell.value = this.getActiveStatusName(gridCell?.data?.is_active);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${e.fileName}.xlsx`);
          });
        });
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.smProductsGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
        this.reloadGrid();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>
